/**
 * DV-AUTH-004: Auth Readiness Gate
 *
 * Session readiness gate to defer user preference writes (theme) until
 * authentication check is complete. Prevents race conditions between
 * session loading and theme persistence.
 */

import { useState, useEffect, useCallback } from 'react';

// Global auth readiness state
let authReady = false;
let authCheckComplete = false;
let isMarkingReady = false; // DV-AUTH-004: StrictMode protection
const pendingOperations: Array<{ operation: () => void; name?: string; id: string }> = [];
const readinessCallbacks = new Set<(isReady: boolean) => void>();
const suppressedErrors: Array<{ error: Error; operationId: string; operationName?: string; timestamp: string }> = []; // DV-AUTH-004: Track suppressed operation errors

/**
 * Mark auth as ready (session check complete)
 * Triggers any pending theme/preference operations
 * DV-AUTH-004: StrictMode idempotent - prevents duplicate processing
 */
export function markAuthReady(): void {
  if (authReady || isMarkingReady) return; // Already ready or currently processing

  isMarkingReady = true; // StrictMode protection
  authReady = true;
  authCheckComplete = true;

  console.log('🔐 Auth Readiness: Session check complete, processing pending operations', {
    pendingCount: pendingOperations.length,
    timestamp: new Date().toISOString()
  });

  // Execute all pending operations with enhanced error tracking
  let successCount = 0;
  let failedCount = 0;

  while (pendingOperations.length > 0) {
    const operationWrapper = pendingOperations.shift();
    if (operationWrapper) {
      try {
        operationWrapper.operation();
        successCount++;
        console.log('🔐 Auth Readiness: Operation executed successfully', {
          operationId: operationWrapper.id,
          operationName: operationWrapper.name || 'unnamed',
          successCount,
          remainingCount: pendingOperations.length
        });
      } catch (error) {
        failedCount++;
        const operationError = error instanceof Error ? error : new Error(String(error));

        // Track suppressed error for debugging
        suppressedErrors.push({
          error: operationError,
          operationId: operationWrapper.id,
          operationName: operationWrapper.name,
          timestamp: new Date().toISOString()
        });

        // Keep only last 20 errors to prevent memory leaks
        if (suppressedErrors.length > 20) {
          suppressedErrors.splice(0, suppressedErrors.length - 20);
        }

        console.error('🔐 Auth Readiness: Error executing pending operation (suppressed)', {
          operationId: operationWrapper.id,
          operationName: operationWrapper.name || 'unnamed',
          error: operationError.message,
          failedCount,
          remainingCount: pendingOperations.length
        });
      }
    }
  }

  console.log('🔐 Auth Readiness: Completed processing pending operations', {
    successCount,
    failedCount,
    totalProcessed: successCount + failedCount,
    suppressedErrorsCount: suppressedErrors.length
  });

  // Notify all readiness callbacks
  readinessCallbacks.forEach(callback => {
    try {
      callback(true);
    } catch (error) {
      console.error('🔐 Auth Readiness: Error in readiness callback:', error);
    }
  });

  isMarkingReady = false; // Reset protection flag
}

/**
 * Mark auth check as failed/complete (no authenticated user)
 * Still triggers pending operations since the check is complete
 * DV-AUTH-004: StrictMode idempotent - prevents duplicate processing
 */
export function markAuthCheckComplete(): void {
  if (authCheckComplete || isMarkingReady) return; // Already complete or currently processing

  isMarkingReady = true; // StrictMode protection
  authCheckComplete = true;

  console.log('🔐 Auth Readiness: Auth check complete (no authenticated user), processing pending operations', {
    pendingCount: pendingOperations.length,
    timestamp: new Date().toISOString()
  });

  // Execute pending operations even if not authenticated with enhanced error tracking
  // Theme persistence should work for anonymous users too
  let successCount = 0;
  let failedCount = 0;

  while (pendingOperations.length > 0) {
    const operationWrapper = pendingOperations.shift();
    if (operationWrapper) {
      try {
        operationWrapper.operation();
        successCount++;
        console.log('🔐 Auth Readiness: Operation executed successfully (anonymous)', {
          operationId: operationWrapper.id,
          operationName: operationWrapper.name || 'unnamed',
          successCount,
          remainingCount: pendingOperations.length
        });
      } catch (error) {
        failedCount++;
        const operationError = error instanceof Error ? error : new Error(String(error));

        // Track suppressed error for debugging
        suppressedErrors.push({
          error: operationError,
          operationId: operationWrapper.id,
          operationName: operationWrapper.name,
          timestamp: new Date().toISOString()
        });

        // Keep only last 20 errors to prevent memory leaks
        if (suppressedErrors.length > 20) {
          suppressedErrors.splice(0, suppressedErrors.length - 20);
        }

        console.error('🔐 Auth Readiness: Error executing pending operation (suppressed, anonymous)', {
          operationId: operationWrapper.id,
          operationName: operationWrapper.name || 'unnamed',
          error: operationError.message,
          failedCount,
          remainingCount: pendingOperations.length
        });
      }
    }
  }

  console.log('🔐 Auth Readiness: Completed processing pending operations (anonymous)', {
    successCount,
    failedCount,
    totalProcessed: successCount + failedCount,
    suppressedErrorsCount: suppressedErrors.length
  });

  // Notify callbacks that check is complete (even if not authenticated)
  readinessCallbacks.forEach(callback => {
    try {
      callback(authCheckComplete);
    } catch (error) {
      console.error('🔐 Auth Readiness: Error in readiness callback:', error);
    }
  });

  isMarkingReady = false; // Reset protection flag
}

/**
 * Check if auth readiness check is complete
 * Returns true if either authenticated OR check completed
 */
export function isAuthReady(): boolean {
  return authCheckComplete;
}

/**
 * Queue an operation to run when auth is ready
 * If already ready, executes immediately
 * DV-AUTH-004: Enhanced with operation identification and error tracking
 */
export function whenAuthReady(operation: () => void, operationName?: string): void {
  const operationId = `op-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

  if (authCheckComplete) {
    // Auth check complete, execute immediately
    try {
      operation();
      console.log('🔐 Auth Readiness: Immediate operation executed successfully', {
        operationId,
        operationName: operationName || 'unnamed'
      });
    } catch (error) {
      const operationError = error instanceof Error ? error : new Error(String(error));

      // Track suppressed error for debugging
      suppressedErrors.push({
        error: operationError,
        operationId,
        operationName,
        timestamp: new Date().toISOString()
      });

      // Keep only last 20 errors to prevent memory leaks
      if (suppressedErrors.length > 20) {
        suppressedErrors.splice(0, suppressedErrors.length - 20);
      }

      console.error('🔐 Auth Readiness: Error executing immediate operation (suppressed)', {
        operationId,
        operationName: operationName || 'unnamed',
        error: operationError.message
      });
    }
  } else {
    // Queue for later execution
    console.log('🔐 Auth Readiness: Queueing operation until session check complete', {
      operationId,
      operationName: operationName || 'unnamed',
      queueLength: pendingOperations.length + 1,
      timestamp: new Date().toISOString()
    });
    pendingOperations.push({ operation, name: operationName, id: operationId });
  }
}

/**
 * React hook for auth readiness state
 * Returns true when session check is complete (regardless of auth status)
 */
export function useAuthReadiness(): {
  isReady: boolean;
  isCheckComplete: boolean;
} {
  const [isReady, setIsReady] = useState(authReady);
  const [isCheckComplete, setIsCheckComplete] = useState(authCheckComplete);

  useEffect(() => {
    const callback = (ready: boolean) => {
      setIsReady(ready);
      setIsCheckComplete(true);
    };

    readinessCallbacks.add(callback);

    // Set initial state in case it changed before we subscribed
    setIsReady(authReady);
    setIsCheckComplete(authCheckComplete);

    return () => {
      readinessCallbacks.delete(callback);
    };
  }, []);

  return { isReady, isCheckComplete };
}

/**
 * Execute operation with auth readiness gate
 * Returns a Promise that resolves when operation is executed
 * DV-AUTH-004: Enhanced with operation naming
 */
export function executeWhenReady<T>(operation: () => T | Promise<T>, operationName?: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const wrappedOperation = async () => {
      try {
        const result = await operation();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    whenAuthReady(wrappedOperation, operationName);
  });
}

/**
 * Hook for deferring operations until auth is ready
 * Useful for theme persistence and other user preferences
 * DV-AUTH-004: Enhanced with operation naming
 */
export function useDeferredOperation() {
  const { isCheckComplete } = useAuthReadiness();

  const defer = useCallback(<T>(operation: () => T | Promise<T>, operationName?: string): Promise<T> => {
    return executeWhenReady(operation, operationName);
  }, []);

  return {
    defer,
    isReady: isCheckComplete,
    queueLength: pendingOperations.length
  };
}

/**
 * Reset auth readiness state (for testing or re-initialization)
 * DV-AUTH-004: Includes StrictMode protection flag reset and suppressed errors
 */
export function resetAuthReadiness(): void {
  authReady = false;
  authCheckComplete = false;
  isMarkingReady = false; // Reset StrictMode protection
  pendingOperations.length = 0; // Clear array
  readinessCallbacks.clear();
  suppressedErrors.length = 0; // Clear suppressed errors

  console.log('🔐 Auth Readiness: State reset', {
    timestamp: new Date().toISOString()
  });
}

/**
 * Get current auth readiness status (for debugging)
 * DV-AUTH-004: Enhanced with suppressed errors and operation details
 */
export function getAuthReadinessStatus(): {
  authReady: boolean;
  authCheckComplete: boolean;
  isMarkingReady: boolean;
  pendingOperations: number;
  callbackCount: number;
  suppressedErrorCount: number;
  pendingOperationDetails: Array<{ id: string; name?: string }>;
} {
  return {
    authReady,
    authCheckComplete,
    isMarkingReady, // DV-AUTH-004: StrictMode protection status
    pendingOperations: pendingOperations.length,
    callbackCount: readinessCallbacks.size,
    suppressedErrorCount: suppressedErrors.length,
    pendingOperationDetails: pendingOperations.map(op => ({ id: op.id, name: op.name }))
  };
}

/**
 * Get suppressed errors for debugging and status reports
 * DV-AUTH-004: Returns recent operation errors with details
 */
export function getSuppressedErrors(): Array<{
  error: Error;
  operationId: string;
  operationName?: string;
  timestamp: string;
}> {
  return [...suppressedErrors]; // Return a copy to prevent mutation
}

/**
 * Clear suppressed errors (for status report management)
 * DV-AUTH-004: Allows clearing error history after status report
 */
export function clearSuppressedErrors(): void {
  const clearedCount = suppressedErrors.length;
  suppressedErrors.length = 0;

  console.log('🔐 Auth Readiness: Cleared suppressed errors', {
    clearedCount,
    timestamp: new Date().toISOString()
  });
}